import { createContext, useContext, useEffect, useState } from 'react';
import { fetchCalibrations } from '../services/api';

const CalibrationPageContext = createContext();

// Calibration factor units per sensor type, keyed by the Select values below.
// Two entries means the sensor is read either metric or imperial and the user
// picks; those are always ordered [metric, imperial] so defaultUnitFor can
// choose from the account's unit system. Single-entry types are fixed.
export const SENSOR_UNITS = {
  'Loadcells': ['lbs/digit'],
  'PressureGauges': ['MPa/digit', 'PSI/digit'],
  'LVDTSensors': ['mV/mm', 'mV/inch'],
  'StrainGauges': ['microstrain/digit'],
};

export const defaultUnitFor = (sensorType, unitSystem = 'imperial') => {
  const options = SENSOR_UNITS[sensorType] || [''];
  if (options.length < 2) return options[0];
  return unitSystem === 'metric' ? options[0] : options[1];
};

// Mirrors useUnits() in GPSLicenses.js: MainLayout owns the Settings toggle,
// writes 'gps-units' and fires 'gps-units-changed' whenever it flips.
const readUnitSystem = () => localStorage.getItem('gps-units') || 'imperial';

export function CalibrationPageProvider({ children }) {
  // Sensor pool + box assignments, loaded from the calibration tables on mount.
  const [testData, setTestData] = useState({});
  const [optionsData, setOptionsData] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  const [sensors, setSensors] = useState({
    'Loadcells': [],
    'PressureGauges': [],
    'LVDTSensors': [],
    'StrainGauges': [],
  });
  const [selectedSensorType, setSelectedSensorType] = useState('Loadcells');
  const [serialNumber, setSerialNumber] = useState('');
  const [calFactor, setCalFactor] = useState('');
  const [unitSystem, setUnitSystem] = useState(readUnitSystem);
  const [calFactorUnit, setCalFactorUnit] = useState(() => defaultUnitFor('Loadcells', readUnitSystem()));
  const [testDate, setTestDate] = useState('');
  const [showPreview, setShowPreview] = useState(false);



  // Track the account's unit system so the metric/imperial default follows the
  // Settings toggle live, the same way the licence pages do.
  useEffect(() => {
    const onChange = () => setUnitSystem(readUnitSystem());
    window.addEventListener('gps-units-changed', onChange);
    return () => window.removeEventListener('gps-units-changed', onChange);
  }, []);

  // Re-default the unit whenever the sensor type or the account's unit system
  // changes. Without this a unit picked for the previous sensor (e.g. lbs/digit)
  // would stick to an LVDT reading in mV/mm. A manual pick survives, since
  // neither dependency changes when the user just clicks the toggle.
  useEffect(() => {
    setCalFactorUnit(defaultUnitFor(selectedSensorType, unitSystem));
  }, [selectedSensorType, unitSystem]);

  const handleSensorChange = (e) => {
    setSelectedSensorType(e.target.value);
  };

  const resetNewSensorDetails = () => {
    setSerialNumber('');
    setCalFactor('');
    setCalFactorUnit(defaultUnitFor(selectedSensorType, unitSystem));
    setTestDate('');
  };

  const addSensor = () => {
    const newSensor = {
      'SerialNumber': serialNumber,
      'CalibrationFactor': calFactor,
      'CalibrationFactorUnit': calFactorUnit,
      'TestDate': testDate,
    };

    setSensors((prev) => ({
      ...prev,
      [selectedSensorType]: [
        ...prev[selectedSensorType],
        newSensor,
      ],
    }));

    // resetNewSensorDetails();
  };

  const removeSensor = (type, indexToRemove) => {
    setSensors((prev) => ({
      ...prev,
      [type]: prev[type].filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  useEffect(() => {
    if (
      sensors['Loadcells'].length > 0 || 
      sensors['PressureGauges'].length > 0 || 
      sensors['LVDTSensors'].length > 0 ||
      sensors['StrainGauges'].length > 0
    ) {
      setShowPreview(true);
    }
    else{
      setShowPreview(false);
    }
  }, [sensors]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCalibrations();
        if (cancelled) return;
        setOptionsData(data.optionsData || {});
        setTestData(data.testData || {});
      } catch (err) {
        if (!cancelled) setDataError(err.message);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const value = {
    testData, setTestData,
    sensors, setSensors,
    selectedSensorType, setSelectedSensorType,
    serialNumber, setSerialNumber,
    calFactor, setCalFactor,
    calFactorUnit, setCalFactorUnit,
    testDate, setTestDate,
    showPreview, setShowPreview,
    optionsData,
    dataLoading, dataError,
    handleSensorChange,
    resetNewSensorDetails,
    addSensor,
    removeSensor,
  };


  return (
    <CalibrationPageContext.Provider value={value}>
      {children}
    </CalibrationPageContext.Provider>
  );
}

export function useCalibrationState() {
  return useContext(CalibrationPageContext);
}